import { useEffect, useState } from 'react';
import { Icon } from '../Icon';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { getI18n, useTranslation } from 'react-i18next';
import { useGetVesselsGeoInfoMutation, useLazyGetSafetyEventsQuery } from 'app/api/facade';
import { DATE_RANGES, TimePeriodT } from 'app/components/TimePeriodSelect/TimePeriodSelect';
import { setSelectedTarget } from 'app/utils/selectedTargetsSlice';
import { TimePeriodSelect } from 'app/components';
import { SafetyEventInfo } from 'ais-facade-api/api';
import { useAlertHook, useFormatMoment } from 'app/hooks';
import moment from 'moment/moment';
import clsx from 'clsx';

import styles from './SafetyList.module.scss';

type CustomSafetyVesselT = SafetyEventInfo & {
  isSafetyActive: boolean;
  areas: (string | undefined)[];
};

export const SafetyList = ({ setInitCenter }: { setInitCenter: (center: [number, number]) => void }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const language = getI18n().language;
  const formatMoment = useFormatMoment();
  const { addAlert } = useAlertHook();
  const [getSafetyEvents, { data: safetyEventsData, isLoading: isLoadingSafetyEvents, isFetching: isFetchingSafetyEvents }] =
    useLazyGetSafetyEventsQuery();
  const [getVesselInAreas, { isLoading: isLoadingVesselInAreas }] = useGetVesselsGeoInfoMutation();

  const [safetyVessels, setSafetyVessels] = useState<CustomSafetyVesselT[]>([]);
  const [timePeriod, setTimePeriod] = useState<TimePeriodT>('24h');
  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const datetimeRange = DATE_RANGES[timePeriod];

  const isSafetyActive = (lastSignal: string | undefined) => {
    if (!lastSignal) return false;
    const signalTime = moment(lastSignal);
    const now = moment();
    return now.diff(signalTime, 'hours') < 24;
  };
  const onItemClick = (vessel: CustomSafetyVesselT) => {
    if (vessel?.registryData?.specialType && vessel?.registryData?.specialType === 'ATON') {
      dispatch(setSelectedTarget({ target: { object: vessel }, type: 'aton' }));
    } else {
      dispatch(setSelectedTarget({ target: { object: vessel }, type: 'vessel' }));
    }
  };
  const handleCenter = (vessel: CustomSafetyVesselT) => {
    if (typeof vessel?.position?.latitude === 'number' && typeof vessel?.position?.longitude === 'number') {
      localStorage.setItem('center', JSON.stringify([vessel.position.longitude, vessel.position.latitude]));
      setInitCenter([vessel.position.longitude, vessel.position.latitude]);
    }
  };
  useEffect(() => {
    const handleData = async () => {
      if (safetyEventsData?.results) {
        const vesselsWithAlerts = safetyEventsData.results
          .filter((event) => event.safety?.lastSignal && event.mmsi)
          .map((event) => ({
            ...event,
            isSafetyActive: isSafetyActive(event.safety?.lastSignal),
            areas: [],
          }));

        const points = vesselsWithAlerts
          .filter(
            (vessel): vessel is typeof vessel & { mmsi: number; position: { latitude: number; longitude: number } } =>
              typeof vessel.mmsi === 'number' &&
              vessel.position != null &&
              typeof vessel.position.latitude === 'number' &&
              typeof vessel.position.longitude === 'number',
          )
          .map((vessel) => ({
            key: vessel.mmsi.toString(),
            latitude: vessel.position.latitude,
            longitude: vessel.position.longitude,
          }));

        if (points.length > 0) {
          try {
            const zonesResponse = await getVesselInAreas({
              body: { points },
              type: ['IN_AREA'],
            }).unwrap();

            const vesselsWithZones = vesselsWithAlerts.map((vessel) => ({
              ...vessel,
              areas:
                zonesResponse.results?.find((area) => area.key === vessel.mmsi?.toString())?.inAreas?.map((zone) => zone.name) ||
                [],
            }));

            setSafetyVessels(vesselsWithZones);
          } catch (error) {
            addAlert(error);
            setSafetyVessels(vesselsWithAlerts);
          }
        } else {
          setSafetyVessels(vesselsWithAlerts);
        }
      }
    };

    handleData();
  }, [safetyEventsData]);

  useEffect(() => {
    setSafetyVessels([]);
    getSafetyEvents({ datetime: datetimeRange })
      .unwrap()
      .catch((error: Error) => addAlert(error));
  }, [timePeriod]);

  useEffect(() => {
    getSafetyEvents({ datetime: datetimeRange })
      .unwrap()
      .catch((error: Error) => addAlert(error));
  }, [language]);

  const isLoading = isLoadingSafetyEvents || isFetchingSafetyEvents || isLoadingVesselInAreas;
  const noData = !isLoading && !safetyVessels.length;

  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <Icon name="safety-sidebar"></Icon>
        {`${t('EmergencySignals')}`}
      </div>
      <div className={styles.line_active_value}>
        <div>{`${t('Period')}:`}</div>
        <div className={styles.dots} />
        <TimePeriodSelect timePeriod={timePeriod} setTimePeriod={setTimePeriod} />
      </div>

      {isLoading ? (
        <div className={styles.loading}>{`${t('DataIsLoading')}`}</div>
      ) : noData ? (
        <p className={styles.no_data}>{t('NoResultsFound')}</p>
      ) : (
        <ul className={styles.list}>
          {safetyVessels.map((vessel) => {
            const isSafetyActive = vessel.isSafetyActive;
            const lastSignal = vessel?.safety?.lastSignal;
            return (
              <li
                key={vessel.mmsi}
                className={styles.item}
                onClick={() => onItemClick(vessel)}
                aria-selected={selectedTarget?.target?.object?.mmsi === vessel?.mmsi}
              >
                <div>
                  <Icon
                    size="14px"
                    name="safety"
                    color={isSafetyActive ? 'var(--data-active-color)' : 'var(--default-primary)'}
                  />
                </div>
                <div className={styles.safety_content}>
                  <div className={styles.safety_content_title}>
                    <div className={styles.title_container}>
                      <strong className={styles.vesselName}>{vessel.registryData?.name || vessel.mmsi}</strong>{' '}
                      {vessel.meta?.type}
                    </div>
                    <div className={styles.aim_icon}>
                      <Icon
                        name="aim"
                        size="14px"
                        color={
                          vessel?.position?.longitude && vessel.position.latitude
                            ? 'var(--default-primary)'
                            : 'var(--main-menu-disabled)'
                        }
                        onClick={() => handleCenter(vessel)}
                      />
                    </div>
                  </div>
                  {vessel.areas && vessel.areas.length > 0 && (
                    <div className={styles.areas}>{`${t('Areas')}:\u0020${vessel.areas.join(', ')}`}</div>
                  )}
                  <div
                    className={clsx(styles.line_value, isSafetyActive ? styles.active_safetty : '')}
                    title={vessel?.safety?.text}
                  >
                    {isSafetyActive ? <strong>{`${t('Alarm')}`}</strong> : `${t('Alarm')}`}
                    {!isSafetyActive && `${t('InactiveTag')}`}
                    <div className={styles.dots}></div>
                    {lastSignal ? formatMoment(moment(lastSignal)) : <>&mdash;</>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
